/**
 * **Die Kennzahlen oben auf „Strom & Erzeugung"** (HM-156) — die drei Karten aus
 * `ui_kits/dashboard/KennzahlenKarten.jsx`: Erzeugungsanlagen · Netzbezug ·
 * Verbrauch. Groß die Energie des Zeitraums, oben rechts die Leistung jetzt,
 * darunter die Teile mit Leistung und Energie.
 *
 * **Gerechnet wird hier nichts, nur zusammengestellt.** Jede Zahl ist eine
 * Ergebnisgröße des Messkonzepts, eine Angabe der Geräte hinter dem
 * Erzeugungszähler (`lib/generation-split.ts`) oder ein Momentanwert
 * (`lib/live-now.ts`).
 *
 * **Die erste Karte heißt „Erzeugungsanlagen" und zeigt den Erzeugungszähler**
 * (Auftraggeber, 0.54.1). Er zählt die Energie, die wirklich abgegeben wurde —
 * die eingespeiste eingeschlossen; „Eigenversorgung" versprach, sie sei im Haus
 * geblieben, und „Erzeugung" passt nicht zu einem Speicher, der nichts erzeugt.
 * Erzeugungsanlage ist der Glossarbegriff für PV **und** Speicher (ADR 0038).
 * Eine eigene Summe aus Erzeugung, Entladung und Ladung gibt es nicht mehr:
 *
 * - **Steht der Speicher hinter dem Erzeugungszähler** (HM-133), zählt der
 *   Zähler PV und Akku netto, und die Zeilen PV (brutto) und Akku erklären die
 *   große Zahl (`battery.inMeter`).
 * - **Hat der Speicher einen eigenen Zähler**, enthält die große Zahl nur die
 *   PV; der Akku ist eine Angabe daneben und kein Teil von ihr — die Karte setzt
 *   ihn deshalb ab.
 *
 * **Fehlt ein Wert, fehlt er** (Regel 8): ohne Momentanwert keine Null, sondern
 * nichts.
 *
 * **Karten gibt es je Seite der Bilanz, die die Anlage führt** (HM-176). Bis
 * dahin galt alles oder nichts: Ohne Erzeugung fiel eine Anlage auf die alten
 * Kacheln zurück — ein Haus mit „Nur Netzbezug" sah anders aus als das daneben
 * mit PV. Jetzt steht „Erzeugungsanlagen" nur mit einer Erzeugung, „Netzbezug"
 * mit Netzbezug oder Einspeisung (die Zeile Einspeisung nur, wenn sie gezählt
 * wird) und „Verbrauch" mit einem Verbrauch — Eigenverbrauch und Netzanteil
 * darin nur mit einer Erzeugung: Ohne sie gibt es keinen Autarkiegrad, und ein
 * „Netz 100 %" behauptete, es sei kein PV-Strom im Haus geblieben, wo ihn nur
 * niemand misst (HM-171).
 *
 * **Der Anteil am Verbrauch ist der Autarkiegrad**, so wie der Server ihn
 * rechnet (ein Verhältnis der Summen, ADR 0025); der Netzanteil ist sein Rest.
 * Aus `Eigenverbrauch ÷ Verbrauch` neu gerechnet, stünde neben dem Autarkiegrad
 * eine zweite, um eine Viertelstunde Abdeckung abweichende Zahl für dieselbe
 * Frage.
 */
import type { GenerationSplit } from "./generation-split.js";
import type { LiveNow } from "./live-now.js";

export interface KeyFigureTotals {
  /** Welche Größen das Messkonzept führt — fehlen ist etwas anderes als `null`. */
  readonly present: {
    readonly consumption: boolean;
    readonly gridImport: boolean;
    readonly gridExport: boolean;
  };
  /** Der Erzeugungszähler — die Ergebnisgröße `generation`. */
  readonly generationWh: number | null;
  readonly consumptionWh: number | null;
  readonly selfUseWh: number | null;
  readonly gridImportWh: number | null;
  readonly gridExportWh: number | null;
  /** 0…1 — `null`, wenn der Server keinen Autarkiegrad nennt. */
  readonly autarky: number | null;
}

export interface KeyFigures {
  /** Welche Karten stehen — mindestens eine, sonst gibt `keyFigures` `null`. */
  readonly cards: {
    readonly supply: boolean;
    readonly grid: boolean;
    readonly consumption: boolean;
  };
  readonly supply: {
    /** Der Erzeugungszähler im Zeitraum, Wh. */
    readonly totalWh: number | null;
    /** Seine Leistung jetzt, W. */
    readonly nowW: number | null;
    /** PV — brutto aus den Geräten, wenn der Speicher hinter dem Zähler steht. */
    readonly pvWh: number | null;
    readonly pvNowW: number | null;
    /** `null` ohne Speicher. */
    readonly battery: {
      /** Ob der Speicher im Erzeugungszähler mitgezählt wird (HM-133). */
      readonly inMeter: boolean;
      readonly dischargedWh: number | null;
      readonly chargedWh: number | null;
      /** Leistung jetzt mit Vorzeichen: positiv lädt, negativ entlädt. */
      readonly nowW: number | null;
    } | null;
  };
  readonly grid: {
    readonly importWh: number | null;
    readonly exportWh: number | null;
    readonly importNowW: number | null;
    readonly exportNowW: number | null;
    /** Ob der Netzbezug gezählt wird — ohne ihn heißt die Karte „Einspeisung" und zeigt diese groß. */
    readonly hasImport: boolean;
    /** Ob die Einspeisung gezählt wird — ohne sie fehlt ihre Zeile. */
    readonly hasExport: boolean;
    /** Der Netzbezug nach Tarif (HM-167) — Teile des Bezugs, leer ohne Tarifzählwerke. */
    readonly tariffs: readonly { readonly label: string; readonly fade: number; readonly wh: number | null }[];
  };
  readonly consumption: {
    readonly totalWh: number | null;
    readonly nowW: number | null;
    readonly selfUseWh: number | null;
    readonly selfUseNowW: number | null;
    readonly gridImportWh: number | null;
    /** Autarkiegrad 0…1 — Anteil des Eigenverbrauchs am Verbrauch. */
    readonly selfShare: number | null;
    /** Ob Eigenverbrauch und Netzanteil darunter stehen — nur mit einer Erzeugung. */
    readonly split: boolean;
  };
}

/**
 * Die Karten — oder `null`, wenn die Anlage keine einzige Seite der Bilanz
 * führt. `split` ist `null` genau dann, wenn es keine Erzeugung gibt.
 */
export function keyFigures(
  totals: KeyFigureTotals,
  split: GenerationSplit | null,
  now: LiveNow,
  tariffs: readonly { readonly label: string; readonly fade: number; readonly wh: number | null }[] = [],
): KeyFigures | null {
  const cards = {
    supply: split !== null,
    grid: totals.present.gridImport || totals.present.gridExport,
    consumption: totals.present.consumption,
  };
  if (!cards.supply && !cards.grid && !cards.consumption) return null;

  const battery = split?.hasBattery === true
    ? {
        inMeter: split.source === "devices",
        dischargedWh: split.batteryWh,
        chargedWh: split.batteryChargedWh,
        nowW: split.nowBatteryW,
      }
    : null;

  return {
    cards,
    supply: {
      totalWh: totals.generationWh,
      nowW: now.generation ?? null,
      pvWh: split?.pvWh ?? null,
      pvNowW: split?.nowPvW ?? null,
      battery,
    },
    grid: {
      importWh: totals.gridImportWh,
      exportWh: totals.gridExportWh,
      importNowW: now.grid_import ?? null,
      exportNowW: now.grid_export ?? null,
      hasImport: totals.present.gridImport,
      hasExport: totals.present.gridExport,
      tariffs,
    },
    consumption: {
      totalWh: totals.consumptionWh,
      nowW: now.consumption ?? null,
      selfUseWh: totals.selfUseWh,
      selfUseNowW: now.self_consumption ?? null,
      gridImportWh: totals.gridImportWh,
      selfShare: totals.autarky === null ? null : Math.min(Math.max(totals.autarky, 0), 1),
      split: cards.supply,
    },
  };
}

/**
 * Was jetzt am Netz fließt — eine Richtung, nie ein Vorzeichen. Speist das Haus
 * ein, steht dort „Einspeisung" und die Einspeiseleistung; sonst der Bezug ohne
 * Wort, denn die Karte heißt schon so.
 */
export function gridFlowNow(
  importW: number | null,
  exportW: number | null,
): { watts: number | null; word: string | undefined } {
  if (exportW !== null && exportW > 0 && (importW === null || exportW > importW)) {
    return { watts: exportW, word: "Einspeisung" };
  }
  return { watts: importW, word: undefined };
}
